import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ExternalLink, Eye, Pencil, Trash2, UserCheck } from "lucide-react";
import { Borrower } from "@/types/borrower";
import { RootState } from "@/store/store";
import { useDeleteBorrower } from "@/hooks/queries";
import { warmAdminAppDetailTab } from "@/pages/admin/adminAppDetailTab";
import AssignUnderwriterDialog from "./AssignUnderwriterDialog";
import DeleteBorrowerDialog from "./DeleteBorrowerDialog";
import { ContextMenuItem, ContextMenuSeparator } from "@/components/ui/context-menu";
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  handleApplicationActivate,
  openApplication,
  openApplicationForEdit,
} from "./openApplication";

export function useBorrowerDealActions() {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const deleteBorrowerMutation = useDeleteBorrower();

  const [menuBorrower, setMenuBorrower] = useState<Borrower | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  const isAdminOrUnderwriter =
    user?.role === "admin" || user?.role === "admin_support" || user?.role === "underwriter";
  const canAssign = user?.role === "admin";
  const canDelete = isAdminOrUnderwriter;

  const open = (borrower: Borrower, opts?: { newTab?: boolean }) => {
    openApplication(navigate, borrower, opts);
  };

  const view = (borrower: Borrower) => {
    openApplication(navigate, borrower);
  };

  const edit = (borrower: Borrower) => {
    openApplicationForEdit(navigate, borrower);
  };

  const activate = (borrower: Borrower, e: React.MouseEvent, onSameTab?: () => void) => {
    handleApplicationActivate(e, navigate, borrower, () => {
      if (onSameTab) {
        onSameTab();
        return;
      }
      openApplication(navigate, borrower);
    });
  };

  /** Same-tab open used by the applications table (marks read via parent). */
  const activateForTable = (
    borrower: Borrower,
    e: React.MouseEvent,
    onRowClick: (borrowerId: number) => void
  ) => {
    handleApplicationActivate(e, navigate, borrower, () => {
      warmAdminAppDetailTab(borrower.id, borrower.app_status);
      onRowClick(borrower.id);
    });
  };

  const requestAssign = (borrower: Borrower) => {
    setMenuBorrower(borrower);
    setAssignDialogOpen(true);
  };

  const requestDelete = (borrower: Borrower) => {
    setMenuBorrower(borrower);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (menuBorrower) deleteBorrowerMutation.mutate(menuBorrower.id);
    setDeleteDialogOpen(false);
    setMenuBorrower(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setMenuBorrower(null);
  };

  const dialogs = (
    <>
      <DeleteBorrowerDialog
        open={deleteDialogOpen}
        borrowerName={menuBorrower?.company_name}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
      <AssignUnderwriterDialog
        open={assignDialogOpen}
        borrower={menuBorrower}
        onClose={() => {
          setAssignDialogOpen(false);
          setMenuBorrower(null);
        }}
      />
    </>
  );

  return {
    canAssign,
    canDelete,
    open,
    view,
    edit,
    activate,
    activateForTable,
    requestAssign,
    requestDelete,
    dialogs,
  };
}

interface BorrowerDealMenuItemsProps {
  borrower: Borrower;
  variant: "context" | "dropdown";
  canAssign: boolean;
  canDelete: boolean;
  onOpenNewTab: (borrower: Borrower) => void;
  onView: (borrower: Borrower) => void;
  onEdit: (borrower: Borrower) => void;
  onAssign: (borrower: Borrower) => void;
  onDelete: (borrower: Borrower) => void;
}

/** Shared Open / View / Edit / Assign / Delete items for context + dropdown menus. */
export function BorrowerDealMenuItems({
  borrower,
  variant,
  canAssign,
  canDelete,
  onOpenNewTab,
  onView,
  onEdit,
  onAssign,
  onDelete,
}: BorrowerDealMenuItemsProps) {
  const Item = variant === "context" ? ContextMenuItem : DropdownMenuItem;
  const Separator = variant === "context" ? ContextMenuSeparator : DropdownMenuSeparator;

  return (
    <>
      <Item onClick={() => onOpenNewTab(borrower)}>
        <ExternalLink className="mr-2 h-4 w-4 text-muted-foreground" />
        Open in new tab
      </Item>
      <Item onClick={() => onView(borrower)}>
        <Eye className="mr-2 h-4 w-4 text-muted-foreground" />
        View
      </Item>
      <Item onClick={() => onEdit(borrower)}>
        <Pencil className="mr-2 h-4 w-4 text-muted-foreground" />
        Edit
      </Item>
      {canAssign && (
        <Item onClick={() => onAssign(borrower)}>
          <UserCheck className="mr-2 h-4 w-4 text-muted-foreground" />
          Assign Underwriter
        </Item>
      )}
      {canDelete && (
        <>
          <Separator />
          <Item
            className="text-destructive focus:text-destructive"
            onClick={() => onDelete(borrower)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Item>
        </>
      )}
    </>
  );
}
